import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { UserPlus, Upload, Link2, Stamp, FileText, Clock } from 'lucide-react';
import { GlassCard } from '@/components/ui/GlassCard';
import { Button } from '@/components/ui/Button';
import { StatusPill } from '@/components/ui/StatusPill';
import { KpiCard } from './KpiCard';
import { KPIS, EXPIRING_DOCUMENTS, RECENT_RETURNS } from './demoData';

export function DashboardPage() {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-7xl space-y-6 pt-2">
      {/* Hero header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
      >
        <div>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">
            {t('dashboard.greeting')}, <span className="text-gradient">VaulTex</span>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm"><UserPlus className="h-4 w-4" />{t('actions.newClient')}</Button>
          <Button size="sm" variant="glass"><Upload className="h-4 w-4" />{t('actions.uploadDocument')}</Button>
          <Button size="sm" variant="gold"><Stamp className="h-4 w-4" />{t('actions.newNotarization')}</Button>
        </div>
      </motion.div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {KPIS.map((k, i) => (
          <KpiCard key={k.key} labelKey={k.key} value={k.value} delta={k.delta} tone={k.tone} index={i} />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Recent tax returns */}
        <GlassCard className="lg:col-span-2" delay={0.1}>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display font-semibold">{t('dashboard.recentActivity')}</h2>
            <Button size="sm" variant="ghost">{t('actions.viewAll')}</Button>
          </div>
          <ul className="divide-y divide-border/60">
            {RECENT_RETURNS.map((r) => (
              <li key={r.id} className="flex items-center gap-3 py-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-muted/50 text-primary">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{r.client}</p>
                  <p className="text-xs text-muted-foreground">Tax Return {r.year} · {r.season}</p>
                </div>
                <StatusPill status={r.status} />
              </li>
            ))}
          </ul>
        </GlassCard>

        {/* Expiring documents — alertas antes de vencimiento */}
        <GlassCard delay={0.16}>
          <div className="mb-4 flex items-center gap-2">
            <Clock className="h-4 w-4 text-warning" />
            <h2 className="font-display font-semibold">{t('dashboard.expiringDocuments')}</h2>
          </div>
          <ul className="space-y-3">
            {EXPIRING_DOCUMENTS.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{d.title}</p>
                  <p className="text-xs text-muted-foreground">{d.expiryDate}</p>
                </div>
                <StatusPill status={d.status} />
              </li>
            ))}
          </ul>
          <Button variant="glass" size="sm" className="mt-4 w-full">
            <Link2 className="h-4 w-4" />{t('actions.createPortalLink')}
          </Button>
        </GlassCard>
      </div>
    </div>
  );
}
